import React, { useEffect, useMemo, useState } from 'react';
import {
  History as HistoryIcon, RefreshCw, Undo2, Download, Search,
  DollarSign, TrendingUp, Tag, Power, Globe, Activity, CheckCircle2, RotateCcw, AlertCircle
} from 'lucide-react';
import { apiCallJson } from '../utils/api';
import { useTheme } from '../context/ThemeContext';

const CATEGORY_META = {
  bid:     { label: 'Bid',     Icon: TrendingUp, color: '#f59e0b' },
  budget:  { label: 'Budget',  Icon: DollarSign, color: '#10b981' },
  keyword: { label: 'Keyword', Icon: Tag,        color: '#06b6d4' },
  status:  { label: 'Status',  Icon: Power,      color: '#8b5cf6' },
  network: { label: 'Network', Icon: Globe,      color: '#ec4899' },
  other:   { label: 'Other',   Icon: Activity,   color: '#64748b' }
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'bid', label: 'Bids' },
  { id: 'budget', label: 'Budgets' },
  { id: 'keyword', label: 'Keywords' },
  { id: 'status', label: 'Status' },
  { id: 'other', label: 'Other' }
];

function formatRelative(ts) {
  if (!ts) return '';
  const d = typeof ts === 'string' ? new Date(ts) : ts;
  const diff = Date.now() - d.getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
}

function formatValue(v) {
  if (v === null || v === undefined) return '—';
  return `€${Number(v).toFixed(2)}`;
}

function humanAction(actionType = '') {
  return String(actionType)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export default function AuditLog({ onNavigate }) {
  const { theme } = useTheme();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [undoingId, setUndoingId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiCallJson('/api/audit-log?limit=300');
      if (data?.success) {
        setEntries(data.entries || []);
      } else {
        setError(data?.error || 'Could not load audit log');
      }
    } catch (err) {
      setError(err.message || 'Could not load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return entries.filter(e => {
      if (filter !== 'all' && e.category !== filter) return false;
      if (q) {
        const hay = `${e.title || ''} ${e.actionType || ''} ${e.reasoning || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [entries, filter, search]);

  const stats = useMemo(() => {
    const applied = entries.filter(e => e.status !== 'reverted').length;
    const reverted = entries.filter(e => e.status === 'reverted').length;
    const undoable = entries.filter(e => e.undoable).length;
    return { applied, reverted, undoable, total: entries.length };
  }, [entries]);

  const handleUndo = async (entry) => {
    setUndoingId(entry.id);
    try {
      const data = await apiCallJson(`/api/optimization/undo/${entry.id}`, { method: 'POST', body: {}, timeout: 60000 });
      if (data?.success) {
        setEntries(prev => prev.map(e =>
          e.id === entry.id
            ? { ...e, status: 'reverted', revertedAt: new Date().toISOString(), undoable: false, undoReason: 'Already reverted' }
            : e
        ));
        setToast({ type: 'success', message: data.message || 'Change reverted' });
      } else {
        setToast({ type: 'error', message: data?.error || 'Undo failed' });
      }
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Undo failed' });
    } finally {
      setUndoingId(null);
    }
  };

  const exportCsv = () => {
    const header = ['Date', 'Action', 'Category', 'Title', 'Original', 'Applied', 'Impact', 'Status'];
    const rows = filtered.map(e => [
      e.appliedAt ? new Date(e.appliedAt).toISOString() : '',
      e.actionType || '',
      e.category || '',
      (e.title || '').replace(/"/g, '""'),
      e.originalValue ?? '',
      e.appliedValue ?? '',
      (e.impact || '').toString().replace(/"/g, '""'),
      e.status || ''
    ]);
    const csv = [header, ...rows]
      .map(r => r.map(c => `"${String(c)}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bidoryx-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const c = theme.colors;

  const styles = {
    page: { padding: 40, maxWidth: 1100, margin: '0 auto' },
    card: { background: c.bg.secondary, border: `1px solid ${c.border.primary}`, borderRadius: 16 },
    chip: (active) => ({
      padding: '8px 16px',
      borderRadius: 999,
      border: `1px solid ${active ? 'transparent' : c.border.primary}`,
      background: active ? c.brand.primary : c.bg.tertiary,
      color: active ? '#fff' : c.text.secondary,
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer'
    }),
    secondaryBtn: {
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '10px 16px', borderRadius: 10,
      border: `1px solid ${c.border.primary}`, background: c.bg.tertiary,
      color: c.text.primary, fontSize: 14, fontWeight: 600, cursor: 'pointer'
    }
  };

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12,
            background: 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <HistoryIcon size={24} color="#fff" />
          </div>
          <div>
            <h1 style={{ color: c.text.primary, fontSize: 26, fontWeight: 700, margin: 0 }}>Audit Log</h1>
            <p style={{ color: c.text.secondary, fontSize: 14, margin: '4px 0 0' }}>
              Every change Bidoryx applied to this account — with one-click undo for bids and budgets.
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button style={styles.secondaryBtn} onClick={exportCsv} disabled={filtered.length === 0}>
            <Download size={16} /> Export CSV
          </button>
          <button style={styles.secondaryBtn} onClick={load}>
            <RefreshCw size={16} className={loading ? 'spin' : undefined} /> Refresh
          </button>
        </div>
      </div>

      {/* Stat tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total changes', value: stats.total, color: c.text.primary },
          { label: 'Applied', value: stats.applied, color: '#10b981' },
          { label: 'Reverted', value: stats.reverted, color: '#f59e0b' },
          { label: 'Undoable now', value: stats.undoable, color: '#06b6d4' }
        ].map(t => (
          <div key={t.label} style={{ ...styles.card, padding: 18 }}>
            <div style={{ color: c.text.secondary, fontSize: 13, marginBottom: 6 }}>{t.label}</div>
            <div style={{ color: t.color, fontSize: 28, fontWeight: 700 }}>{t.value}</div>
          </div>
        ))}
      </div>

      {/* Filters + search */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
        {FILTERS.map(f => (
          <button key={f.id} style={styles.chip(filter === f.id)} onClick={() => setFilter(f.id)}>{f.label}</button>
        ))}
        <div style={{ position: 'relative', marginLeft: 'auto' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: 11, color: c.text.tertiary }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search changes…"
            style={{
              padding: '9px 12px 9px 36px', borderRadius: 10, minWidth: 240,
              border: `1px solid ${c.border.primary}`, background: c.bg.tertiary,
              color: c.text.primary, fontSize: 14, outline: 'none'
            }}
          />
        </div>
      </div>

      {error && (
        <div style={{ ...styles.card, padding: 16, marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center', borderColor: 'rgba(239,68,68,0.4)' }}>
          <AlertCircle size={18} color="#ef4444" />
          <span style={{ color: c.text.primary, fontSize: 14 }}>{error}</span>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: c.text.secondary }}>
          <RefreshCw size={36} className="spin" style={{ marginBottom: 12 }} />
          <p>Loading audit log…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ ...styles.card, padding: 48, textAlign: 'center' }}>
          <HistoryIcon size={40} color={c.text.tertiary} style={{ marginBottom: 12 }} />
          <h3 style={{ color: c.text.primary, margin: '0 0 6px' }}>No changes logged yet</h3>
          <p style={{ color: c.text.secondary, fontSize: 14, margin: 0 }}>
            When you apply an AI optimization, it shows up here so you can review or undo it.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(entry => {
            const meta = CATEGORY_META[entry.category] || CATEGORY_META.other;
            const Icon = meta.Icon;
            const reverted = entry.status === 'reverted';
            const hasValues = entry.originalValue !== null && entry.appliedValue !== null;
            return (
              <div key={entry.id} style={{ ...styles.card, padding: 16, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: `${meta.color}1A`, display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <Icon size={20} color={meta.color} />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ color: c.text.primary, fontSize: 15, fontWeight: 600 }}>
                      {entry.title || humanAction(entry.actionType)}
                    </span>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                      background: `${meta.color}1A`, color: meta.color
                    }}>
                      {humanAction(entry.actionType)}
                    </span>
                    {reverted && (
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6,
                        background: 'rgba(245,158,11,0.15)', color: '#f59e0b',
                        display: 'inline-flex', alignItems: 'center', gap: 4
                      }}>
                        <RotateCcw size={11} /> Reverted
                      </span>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 16, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    {hasValues && (
                      <span style={{ color: c.text.secondary, fontSize: 13 }}>
                        {formatValue(entry.originalValue)}
                        <span style={{ margin: '0 6px', color: c.text.tertiary }}>→</span>
                        <span style={{ color: c.text.primary, fontWeight: 600 }}>{formatValue(entry.appliedValue)}</span>
                      </span>
                    )}
                    {entry.impact && (
                      <span style={{ color: '#10b981', fontSize: 13, fontWeight: 600 }}>{entry.impact}</span>
                    )}
                    <span style={{ color: c.text.tertiary, fontSize: 12 }}>
                      {reverted && entry.revertedAt ? `Reverted ${formatRelative(entry.revertedAt)}` : formatRelative(entry.appliedAt)}
                    </span>
                  </div>
                </div>

                <div style={{ flexShrink: 0 }}>
                  {entry.undoable ? (
                    <button
                      onClick={() => handleUndo(entry)}
                      disabled={undoingId === entry.id}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 9,
                        border: `1px solid ${c.border.primary}`, background: c.bg.tertiary,
                        color: c.text.primary, fontSize: 13, fontWeight: 600,
                        cursor: undoingId === entry.id ? 'wait' : 'pointer', opacity: undoingId === entry.id ? 0.6 : 1
                      }}
                    >
                      {undoingId === entry.id
                        ? <><RefreshCw size={14} className="spin" /> Undoing…</>
                        : <><Undo2 size={14} /> Undo</>}
                    </button>
                  ) : reverted ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#10b981', fontSize: 13, fontWeight: 600 }}>
                      <CheckCircle2 size={15} /> Done
                    </span>
                  ) : (
                    <span title={entry.undoReason || ''} style={{ color: c.text.tertiary, fontSize: 12, maxWidth: 150, textAlign: 'right', display: 'inline-block' }}>
                      {entry.undoReason || 'Manual revert'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
          background: toast.type === 'success' ? '#10b981' : '#ef4444',
          color: '#fff', padding: '12px 18px', borderRadius: 10,
          fontSize: 14, fontWeight: 600, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', gap: 8, maxWidth: 360
        }}>
          {toast.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          {toast.message}
        </div>
      )}

      <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
